/**
 * Application URL Parameters
 * 
 * Centralized parsing and access for all URL query parameters.
 * Parse once at app startup, access globally via getAppParams().
 * 
 * Categories:
 * - Loading: ?load=, ?molxyz=, ?smiles= (delegated to urlParser)
 * - Debug: ?debug=1, ?selectdebug=1, etc.
 * - Features: ?bondVerify=1, ?recomputeBondsEveryUpdate=1
 * - Behavior: ?noTutorial=1, ?autoMD=0, ?tutorial=1 (force tutorial on deep-link)
 * 
 * Usage:
 *   // At app startup (init.tsx)
 *   parseAppParams();
 * 
 *   // Anywhere else
 *   const params = getAppParams();
 *   if (params.bondVerify) { ... }
 */

import { parseURLParams as parseLoadingParams } from '../loader/urlParser';
import type { URLParams } from '../loader/types';

/**
 * Debug category configuration
 */
export interface DebugCategories {
  select: boolean;    // Selection & picking
  drag: boolean;      // Atom manipulation & dragging
  factory: boolean;   // Factory initialization & lifecycle
  frame: boolean;     // Frame handling & updates
  timeline: boolean;  // Timeline operations
  ws: boolean;        // WebSocket transport
  sim: boolean;       // Simulation controls
  loader: boolean;    // System/session loading
  render: boolean;    // Rendering & geometry updates
  topology: boolean;  // Topology changes (atom add/remove)
  budget: boolean;    // Budget & focus-aware simulation limits
  naming: boolean;    // Island naming pipeline (run triggers, requests, responses)
  'drag-predict': boolean; // Local bond prediction during drag (predictBondsFromInteraction)
}

/**
 * Complete application parameters
 */
export interface AppParams {
  // ========== Loading ==========
  /** System ID from manifest (?load={id}) */
  load: string | null;
  /** Decoded XYZ content (?molxyz={base64}) - already decoded */
  molxyz: string | null;
  /** SMILES string (?smiles={string}) */
  smiles: string | null;

  // ========== Debug Logging ==========
  /** Master debug flag - enables all categories (?debug=1) */
  debug: boolean;
  /** Individual debug category flags */
  debugCategories: DebugCategories;

  // ========== Features ==========
  /** Enable bond verification mode (?bondVerify=1) */
  bondVerify: boolean;
  /**
   * Force full bond recomputation every frame (?recomputeBondEveryFrame=1).
   * Note: Small PBC systems (< 100 atoms) automatically recompute every frame
   * regardless of this flag. Use this flag to force recompute for larger systems.
   */
  recomputeBondsEveryUpdate: boolean;
  /** Alias for recomputeBondsEveryUpdate (?recomputeBondEveryFrame=1) */
  recomputeBondEveryFrame: boolean;

  // ========== Behavior ==========
  /** Edit mode - disables auto-simulation and tutorial (?edit=1) */
  edit: boolean;
  /** Playback mode - loads a session JSON from URL, hides all UI, loops playback (?playbackURL=path) */
  playbackURL: string | null;
  /** Show molecule labels (?labels=0 to hide, default true) */
  labels: boolean;
  /** Enable mouse/keyboard interaction (?interactive=0 to disable, default true; false in playback mode) */
  interactive: boolean;
  /** Enhanced shading with backlight and higher specular (?fancyShading=1, default false) */
  fancyShading: boolean;
  /**
   * Master gate for experimental, not-yet-GA features (`?experimental=1`,
   * default false). Currently gates the tractor-beam (pairwise bias) UI:
   * the floating panel on two-atom selection AND the rendered bias beams.
   * When off, no panel appears and no beams render even if the backend
   * echoes active biases.
   */
  experimental: boolean;
  /**
   * Disable tutorial overlay.
   *
   * True when any of the following hold:
   *   - `?noTutorial` (with or without `=1`)
   *   - `?edit=1`
   *   - any deep-link parameter is present (`?load=`, `?molxyz=`,
   *     `?smiles=`) — the user is asking for a specific system, so
   *     the tutorial step that force-loads `common_water` would
   *     clobber their choice.
   *
   * Override the deep-link implication with `?tutorial=1`, which keeps
   * the tutorial running on top of the deep-linked system.
   */
  noTutorial: boolean;
  /**
   * Launch a specific tutorial module by id instead of the default
   * (`?tutorialModule=<id>`). Used to reach `hidden` modules that are not
   * shown in the catalog (e.g. `?tutorialModule=build-chains`). Null when
   * absent, in which case the default module is used. An unknown id falls
   * back gracefully (the module navigator validates it and starts no module).
   */
  tutorialModule: string | null;
  /**
   * Whether the left + right side panels (visualization, AI acceleration,
   * etc.) start opened on mount.
   *
   * Rules (highest precedence first):
   *   1. `?panelsOpen=1` → always open. `?panelsOpen=0` → always closed.
   *   2. Explicit `?noTutorial`, `?noTutorial=1`, `?edit=1`, `?gameMode=1`
   *      → open. The user/test runner has explicitly opted out of the
   *      tutorial (or into edit/game mode), so they expect full chrome.
   *   3. Implicit deep-link tutorial-suppression (a bare `?load=`,
   *      `?molxyz=`, or `?smiles=` with no other tutorial-related
   *      flags) → CLOSED. Deep-link demo links should land the user in
   *      a kiosk-style view focused on the molecule, not buried under
   *      panels they didn't ask for.
   *   4. Default (full tutorial about to run) → closed. The tutorial
   *      itself opens the relevant panel as part of its first step.
   *
   * E2E tests that need panels visible alongside `?load=` should pass
   * `&noTutorial=1` (which they already do) or `&panelsOpen=1`.
   */
  panelsOpenDefault: boolean;
  /**
   * Force the TimelineDock to always be visible regardless of
   * `playbackBarOpen` state (`?alwaysShowTimeline=1`). Debug aid for
   * inspecting timeline + reaction-marker behavior without relying on
   * the dock's normal auto-open trigger. Does NOT set `playbackBarOpen`
   * itself, so load/add-atom buttons stay enabled.
   */
  alwaysShowTimeline: boolean;
  /** Auto-start MD after loading (default: true, disable with ?autoMD=0 or ?edit=1) */
  autoMD: boolean;
  /** Prevent default system loading (?noAutoLoad=1) */
  noAutoLoad: boolean;
  /**
   * Enable the molecule-naming pipeline (default `true`; disable with
   * `?naming=0`).
   *
   * The naming pipeline is `IslandDetectionService` — it watches
   * connectivity, partitions the scene into connected components, POSTs
   * each multi-atom island to the standalone naming server
   * (`/api/name-molecules`), and projects results back as
   *   - 3D scene labels (via `IslandLabelManager`)
   *   - the molecule-name string in the `MoleculeInfoBadge`
   *   - perceived bond orders pushed back into `molState.bonds`
   *   - the input feed for the optional reaction detector
   *
   * When this flag is `false`, the orchestrator is constructed but
   * never `enable()`d, so:
   *   - zero `/api/name-molecules` requests fire,
   *   - `getNamedMolecules()` returns `[]` for the lifetime of the page,
   *   - the badge falls back to the Hill formula,
   *   - bond orders perceived by the naming server are not back-applied
   *     (renderer keeps the order it received from the bond engine),
   *   - `?discoverChemistry=1` is a no-op (the detector subscribes to
   *     the naming feed and would never fire; we warn and skip wiring).
   *
   * Polarity matches `?autoMD=0` and `?recomputeBondEveryFrame=0` —
   * only the literal string `0` disables; absent / `=1` / anything else
   * keeps the historical default behavior intact.
   */
  naming: boolean;
  /**
   * Override for the naming-resolver circuit-breaker cooldown, in ms
   * (`?namingBreakerCooldownMs=N`). When the breaker trips on
   * consecutive failures it stays open for this long before letting
   * a probe through. Defaults to 30 s in production.
   *
   * Exists primarily so E2E tests can drop the cooldown to ~100 ms
   * (rather than fake-timer-mocking the whole resolver) when verifying
   * graceful-degradation paths. Negative or non-numeric values are
   * ignored and the production default is used.
   */
  namingBreakerCooldownMs: number | null;
  /**
   * Discover-Chemistry feature flag. **Now always on** — the URL flag
   * is ignored and this field is hard-coded `true`. Reaction detection,
   * the timeline `ReactionMarkers` overlay, the `ReactionLiveAnnouncer`
   * (a11y), and the click-to-replay cinematic are permanently wired.
   * The field is retained on the type for downstream code paths that
   * still branch on it (and so future re-introduction of a runtime
   * toggle does not need to reshape the appParams interface).
   */
  discoverChemistry: boolean;
  /**
   * Number of real (non-interpolated) backend frames a candidate
   * topology must remain unchanged before the reaction detector emits
   * it as a reaction. Default {@link STABILITY_STEPS_DEFAULT} (10).
   * Set to `1` via `?stabilitySteps=1` to disable the gate (legacy
   * "emit on every diff" behaviour) — used by tests that drive
   * synthetic chemistry. Values below `1` are clamped to `1`.
   */
  stabilitySteps: number;
  /**
   * Additional frames the reaction-cinematic includes on each side
   * of the detected reactant→product window when scrubbing the
   * replay (`?discoverReplay=N`, integer ≥ 0; default 0). This is
   * *added on top of* a fixed baseline padding of
   * {@link DISCOVER_REPLAY_BASELINE} frames per side, so the
   * effective preroll/postroll is `BASELINE + N`.
   *
   * - `BASELINE + N` extra frames are walked from before the reactant.
   * - `BASELINE + N` extra frames are walked from after the product.
   *
   * Both extensions are clamped to whatever the live `FrameBuffer`
   * still holds; if fewer frames are available on either side, the
   * cinematic uses what it has and continues. The reverse + forward
   * scrub speeds (≈8 fps forward) are unchanged — a larger window
   * simply takes proportionally longer to play.
   *
   * Tied to {@link discoverChemistry}: this knob has no effect when
   * the master gate is off.
   */
  discoverReplay: number;
  /**
   * Whitespace buffer (in percent) added around the framed reaction
   * cluster when the cinematic pans the camera into a head-on view
   * (`?discoverZoom=N`, integer ≥ 0; default 275 → 275 % extra room
   * on every edge of the perspective frustum, i.e. radius factor of
   * `1 + 2.75 = 3.75 ×` the cluster extent). Larger values zoom
   * out further — useful for big or rapidly-moving reactions; smaller
   * values zoom in tighter.
   *
   * Tied to {@link discoverChemistry}: this knob has no effect when
   * the master gate is off.
   */
  discoverZoom: number;
  /**
   * Master gate for the experimental Game Mode (`?gameMode=1`). When
   * **off** (the default) game-mode code paths are completely dormant —
   * no game services are constructed, the standard viewer UI mounts
   * unchanged, and there is zero runtime cost. When **on**, the
   * standard viewer UI is replaced by the game HUD, the camera is
   * locked top-down, the containment wall is disabled, MD auto-stop
   * is suppressed, and a 30 s session begins automatically with
   * benzene/naphthalene/radical projectiles spawning from the bottom
   * of the screen.
   *
   * All other `game*` knobs below have no effect when this gate is off.
   */
  gameMode: boolean;

  /**
   * Total length of one game session in seconds (`?gameSessionSec=N`,
   * integer ≥ 1; default 30). Reactions detected during this window
   * count toward the user's score. After the timer elapses the round
   * ends, the score is shown, and the user can start another round.
   */
  gameSessionSec: number;

  /**
   * Minimum number of live (in-viewport) projectiles the game-mode
   * spawner aims to keep on screen (`?gameProjectileMin=N`, integer ≥ 0;
   * default 1). When the live count drops below this the spawner
   * immediately spawns one more, subject to {@link gameSpawnCooldownMs}
   * and {@link gameProjectileMax}.
   */
  gameProjectileMin: number;

  /**
   * Hard cap on the number of live projectiles in flight at once
   * (`?gameProjectileMax=N`, integer ≥ {@link gameProjectileMin}; default 4).
   * The spawner refuses to spawn beyond this even when the population
   * is below the minimum, to bound atom-count growth on the renderer.
   */
  gameProjectileMax: number;

  /**
   * Minimum gap (ms) between consecutive spawn calls
   * (`?gameSpawnCooldownMs=N`, integer ≥ 0; default 400). Used only
   * during the start-of-round burst (while live count < `projectileMin`)
   * — once the floor is reached, the steady-state cadence
   * {@link gameSpawnIntervalMs} takes over.
   */
  gameSpawnCooldownMs: number;

  /**
   * Steady-state spawn cadence (ms) once the play area has at least
   * `gameProjectileMin` live projectiles
   * (`?gameSpawnIntervalMs=N`, integer ≥ 0; default 2000). Replaces
   * the old population-driven spawner: at most one new projectile is
   * launched per interval, with X-spacing applied to keep
   * consecutive launches from colliding before they enter the
   * viewport. {@link gameProjectileMax} still acts as a safety cap.
   */
  gameSpawnIntervalMs: number;

  /**
   * Initial upward velocity (Å/fs) per projectile atom
   * (`?gameLaunchSpeed=N`, positive float; default 0.04). Stamped onto
   * `NewAtomSpec.velocity = (0, +vSpawn, 0)` for every atom of every
   * spawn. Larger values shorten transit time across the viewport
   * (more reaction opportunities per unit time, but higher collision
   * energy, which can produce non-physical reaction outcomes).
   */
  gameLaunchSpeed: number;

  /**
   * Half-thickness (Å) of the planar-confinement slab's free zone
   * around z = 0 (`?gameInplaneTol=N`, positive float; default 0.5).
   * Atoms with `|z| <= gameInplaneTolerance` feel zero restoring
   * force; outside that band a soft Hookean pull bends them back to
   * the play surface. Forwarded to `simulation.startMD` on every
   * round start. Pass `0` to disable.
   *
   * Game-mode-only — no UI surface, no effect outside `?gameMode=1`.
   */
  gameInplaneTolerance: number;
  /**
   * Stiffness (eV/Å²) of the planar-confinement slab
   * (`?gameInplaneK=N`, non-negative float; default 10). Backend
   * clamps to a 100 eV/Å² upper bound shared with `containmentK`.
   *
   * **`?gameInplaneK=0` is the documented escape hatch** — disables
   * the slab entirely so we can reproduce "atoms drift out of plane"
   * behavior for debugging projectile motion or comparing against
   * pre-slab gameplay. Game-mode-only.
   */
  gameInplaneK: number;

  /**
   * Background fade percentage applied to atoms and bonds **not**
   * involved in a reaction while the cinematic plays
   * (`?discoverFade=N`, integer 0–100; default 100).
   *
   * Semantics match the other `discover*` knobs — bigger N = bigger
   * effect:
   * - `100` (default) ⇒ peripheral atoms/bonds fade to fully invisible
   *   for the duration of the cinematic (`backgroundOpacity = 0`).
   * - `50` ⇒ peripheral atoms/bonds drop to 50 % alpha.
   * - `0` ⇒ feature disabled; no opacity mask is applied during the
   *   cinematic and the renderer behaves exactly as before.
   *
   * Implementation note: the cinematic applies the fade by calling
   * the existing `MoleculeViewAPI.setOpacityMask` with
   * `focus.atomHashes = event.involvedAtomHashes` and
   * `backgroundOpacity = { atoms: 1 - N/100, bonds: 1 - N/100 }`.
   * No new rendering code path; reuses the renderer's existing
   * dual-master `MODE_SOLID` / `MODE_SOFT` infrastructure. The mask
   * is cleared the moment the cinematic returns to `idle`, so any
   * user-authored opacity (from control messages) is restored
   * automatically by the next control-message tick.
   *
   * Tied to {@link discoverChemistry}: this knob has no effect when
   * the master gate is off.
   */
  discoverFade: number;

  // ========== Performance ==========
  /**
   * Frame interpolation - fixed output FPS (?interpolation=0|1|2|3)
   * 0 = off - passthrough at RAF rate (~60fps visual, frames as backend sends)
   * 1 = fixed 30 fps output (matches typical backend, minimal interpolation)
   * 2 = fixed 60 fps output (2x interpolation when backend is ~30fps)
   * 3 = fixed 120 fps output (4x interpolation when backend is ~30fps)
   *
   * Interpolation only activates when targetFps > backendFps.
   * If backend is faster than target, latest frame is used (rate limiting).
   */
  interpolation: 0 | 1 | 2 | 3;

  // ========== Testing ==========
  /** Artificial latency (ms) injected before each backend frame send (?addLatency=N) */
  addLatency: number;

  // ========== Simulation overrides ==========
  /**
   * Langevin friction coefficient (1/fs) override (?friction=N).
   *
   * When set, beats every other tier in the friction resolution chain
   * (explicit caller opts, XYZ-stamped `dynamics.friction`, and the
   * `DEFAULT_MD_FRICTION` fallback). `?friction=0` forces NVE globally;
   * `?friction>0` forces NVT with that Langevin gamma. `null` ⇒ no
   * override (normal 3-tier resolution applies).
   *
   * Accepted range: `>= 0`. Negative or non-finite ⇒ `null` (no
   * override). See {@link parseFriction}.
   */
  friction: number | null;

  /**
   * MD integrator timestep in fs (?timestep=N). Default 1.0 fs, clamped to
   * `[TIMESTEP_FS_MIN, TIMESTEP_FS_MAX]` (0.05–2.0) — both bounds mirror the
   * backend. URL-only config — there is intentionally no UI control and no
   * runtime setter (mirrors `?friction`). An explicit caller-supplied
   * `timestep_fs` (e.g. the nano-reactor tutorial's calibrated 0.5 fs) still
   * wins over this value. See {@link parseTimestepFs}.
   */
  timestepFs: number;

  // ========== Timeline buffer ==========
  /**
   * Hard byte budget for the timeline frame buffer (?frameBufferMaxBytes=MB).
   *
   * Specified in MB. The buffer keeps at most this many bytes of stored
   * frames; oldest frames are evicted first when the budget is exceeded.
   * Default `64` MB. Pass `0` to disable the byte cap (the count cap
   * still applies). Useful safety rail on large systems where the GNN
   * edge arrays in each stored frame can otherwise let the timeline
   * accumulate hundreds of MB.
   */
  frameBufferMaxBytes: number;

  // ========== Jitter Buffer ==========
  /**
   * Jitter buffer delay in ms (?jitter=N).
   * Default: 700ms (enabled).
   * ?jitter=0 disables the jitter buffer.
   * Positive value enables JitterBufferStage with that delay.
   * Typical values: 100-1000ms for network jitter smoothing.
   */
  jitter: number;

  /**
   * Show jitter buffer debug panel (?debugJitter=1).
   * Shows latency stats, sparklines, and buffer depth.
   * Default: false (hidden).
   */
  debugJitter: boolean;

  // ========== Containment Ball ==========
  /**
   * Spherical containment ball radius in Å (?ballRadius=N).
   *
   * Soft flat-bottom harmonic wall around the initial mass-weighted COM
   * — keeps atoms from drifting off into vacuum during MD without
   *  introducing any other bias. Backend-side; see backend
   * `containment.py`.
   *
   * Default when the param is absent: `BALL_RADIUS_DEFAULT` (30 Å).
   *
   * Sentinels:
   *   `0`     — auto: backend resolves at MD start to `max_i |r_i - COM| + 3 Å`.
   *   `> 0`   — literal radius forwarded to backend (clamped to 1000 Å).
   *
   * The wall is only attached when `ballK > 0`; this field is ignored
   * otherwise. Live updates take effect on the next MD start (slider
   * UI for live tuning lands later).
   */
  ballRadius: number;

  /**
   * `true` iff the user explicitly set `?ballRadius=` in the URL. When
   * pinned, the radius is treated as authoritative for the lifetime of
   * the page: file-load policy (`applyBallRadiusFromFile`) and session
   * restore (`loadFromFile`) both no-op on the radius. Slider drags
   * still write the displayed value via `setBallRadiusRuntime`, but
   * they do NOT clear the pin (the pin is a one-shot URL contract).
   *
   * Pinning rules (intentional):
   *   - `?ballRadius=N` always wins, including over saved sessions.
   *   - Slider drag does NOT pin.
   *   - Tutorial `setBallRadiusRuntime` does NOT pin.
   *   - File `ball_radius=N` does NOT pin (it just applies until next load).
   */
  ballRadiusPinned: boolean;

  /**
   * Half-angle (degrees) of the transparent "hole" the containment-sphere
   * renderer carves out of the camera-facing cap (?camerahole=N or
   * ?cameraHole=N).
   *
   * The wireframe sphere only renders at all when at least one atom has
   * escaped the wall (see `containmentRenderer.ts`). When it does, every
   * vertex's alpha is `max(cameraAlpha, breachAlpha)`. `cameraAlpha`
   * smoothsteps from 0 across a transparent cap centered on the
   * camera-facing pole up to a peak wash on the far side. This param
   * controls the half-angle of that transparent cap.
   *
   * Mapping (let `α = camerahole` in radians):
   *   `HOLE_END_DOT  = -cos(α)`   — vertices within angle α of the
   *                                  camera-facing pole get alpha 0.
   *   `BACK_FULL_DOT = HOLE_END_DOT + SMOOTHSTEP_WIDTH` (clamped to 1).
   *
   * Range: `0..180`. `0` ⇒ no hole (only the literal pole point is
   * transparent); `90` ⇒ entire camera-facing hemisphere is a hole;
   * `180` ⇒ entire sphere is transparent (effectively hides the
   * camera-side wash; breach hot-spots still pop).
   *
   * Default: `CAMERA_HOLE_DEFAULT` (78°), giving the wide "look straight
   * through into the molecule" hole the new design ships with.
   */
  cameraHole: number;

  /**
   * Spherical containment ball stiffness in eV/Å² (?ballK=N).
   *
   * Default when the param is absent: `BALL_K_DEFAULT` (0.5 eV/Å²),
   * meaning the wall is ON by default. Pass `?ballK=0` to disable.
   *
   * `0` ⇒ wall disabled; `> 0` ⇒ wall enabled with that stiffness
   * (clamped to 100 on the backend).
   *
   * The wall reports its energy through ASE's standard constraint
   * plumbing, so the frontend energy chart automatically shows the
   * NVE-conserved total (`U_UMA + U_wall`).
   */
  ballK: number;

  /**
   * Nano-reactor mode (Wang et al., Nature Chem 2014). When enabled,
   * the backend periodically modulates the containment ball radius
   * between `nanoReactorRMin` and `nanoReactorRMax` over a cycle of
   * `nanoReactorPeriodSteps` MD steps, driving reactive collisions.
   * Requires `ballK > 0`; the panel UI hides the toggle otherwise.
   *
   * Per the project policy, this field is reset to `false` on every
   * system load — it is not sticky across loaded molecules. Tutorials
   * may later opt in to pre-configured reactor setups, but that's a
   * v2 concern.
   */
  nanoReactorEnabled: boolean;
  /** Lower bound of R(t), Å. Active only when nanoReactorEnabled. */
  nanoReactorRMin: number;
  /** Upper bound of R(t), Å. Active only when nanoReactorEnabled. */
  nanoReactorRMax: number;
  /** Cycle length in integer MD steps. Active only when nanoReactorEnabled. */
  nanoReactorPeriodSteps: number;

  /**
   * Total system charge (e) sent to the UMA model. Integer in
   * [CHARGE_MIN, CHARGE_MAX]; default `CHARGE_DEFAULT` (neutral). Forwarded
   * on every Start and applied to idle single-point energy too. The
   * backend clamps to its own CHARGE_RANGE and echoes the frozen value via
   * `Frame.charge` (see `setLastChargeSpinState`).
   */
  charge: number;
  /**
   * Total system spin (UMA convention — multiplicity 2S+1 for the omol
   * task). Integer in [SPIN_MIN, SPIN_MAX]; default `SPIN_DEFAULT`
   * (singlet). Echoed via `Frame.spin`.
   */
  spin: number;
}

/** Cached params - set by parseAppParams() */
let cachedParams: AppParams | null = null;

/**
 * Parse interpolation level from URL param
 * Valid values: 0, 1, 2, 3 (defaults to 3 if not specified)
 */
function parseInterpolationLevel(value: string | null | undefined): 0 | 1 | 2 | 3 {
  if (!value) return 3;
  const num = parseInt(value, 10);
  if (num >= 0 && num <= 3) return num as 0 | 1 | 2 | 3;
  return 3;
}

/**
 * Parse addLatency from URL param
 * Valid values: 0-5000 (ms), defaults to 0 if not specified
 */
function parseAddLatency(value: string | null | undefined): number {
  if (!value) return 0;
  const num = parseInt(value, 10);
  if (Number.isNaN(num) || num < 0) return 0;
  return Math.min(num, 5000);
}

/** Upper bound for ?ballRadius=N (Å). Mirrors backend clamp in ws_app. */
export const BALL_RADIUS_MAX = 1000;
/** Upper bound for ?ballK=N (eV/Å²). Mirrors backend clamp in ws_app. */
export const BALL_K_MAX = 100;
/** Default `?timestep` when the URL omits it (fs). */
export const TIMESTEP_FS_DEFAULT = 1.0;
/**
 * Minimum `?timestep=N` (fs). Mirrors the backend `MIN_TIMESTEP_FS` floor
 * (`ws_app.py`) so the value on the wire matches what actually runs — a
 * sub-floor request is raised here instead of being silently raised
 * server-side. Only guards against a degenerate dt; realistic values like
 * 0.25 fs pass through unchanged.
 */
export const TIMESTEP_FS_MIN = 0.05;
/**
 * Upper bound for `?timestep=N` (fs). The backend has NO maximum clamp —
 * it only enforces a 1.0 fs minimum (`ws_app.py` `max(1.0, …)`) and a
 * post-hoc instability guard that trips `MD_INSTABILITY` when a single
 * step displaces an atom > 5 Å (`worker_pool.py`). So the frontend is the
 * only place that can reject a runaway dt cleanly; ~2 fs is a safe ceiling
 * for the VelocityVerlet / Langevin integrators in use.
 */
export const TIMESTEP_FS_MAX = 2.0;
/**
 * Charge / spin UI bounds. These are the *slider* bounds, intentionally
 * narrower than the model's accepted CHARGE_RANGE [-100,100] / SPIN_RANGE
 * [0,100] (see backend `model_runtime`). Spin is the UMA multiplicity, so
 * the minimum is 1 (singlet) — a sub-1 value would be rejected by the
 * model. The backend re-clamps defensively regardless.
 */
export const CHARGE_MIN = -2;
export const CHARGE_MAX = 2;
export const CHARGE_DEFAULT = 0;
export const SPIN_MIN = 1;
export const SPIN_MAX = 5;
export const SPIN_DEFAULT = 1;
/** Default `?ballRadius` when the URL omits it (Å). */
export const BALL_RADIUS_DEFAULT = 30;
/**
 * Default `?ballK` when the URL omits it (eV/Å²).
 *
 * Interpreted by the backend as the **per-hydrogen-mass** force
 * constant in the Martinez mass-weighted wall (Wang 2014 nanoreactor
 * convention). A carbon atom feels 12× this value, oxygen 16×, etc.
 *
 * Tuned to 10.0 — strong enough that atoms outside the wall snap
 * back within ~5 fs, matching the user-perceived "the wall is doing
 * its job" timescale. Integrator stability: ω·dt for the wall is
 * √(k/m_ref · 9.65e-3) · dt = √(0.0965) · 1 ≈ 0.31, well under the
 * 0.5 threshold where `nanoreactor.min_safe_radius` starts adding
 * buffer. Identical for all species under mass-weighting (the per-
 * atom acceleration depends only on k/m_ref, not on m_n).
 */
export const BALL_K_DEFAULT = 5.0;

/** Default `?frameBufferMaxBytes` when the URL omits it (MB). */
export const FRAME_BUFFER_MAX_BYTES_DEFAULT_MB = 64;
/** Upper clamp for `?frameBufferMaxBytes` (MB) — guards against typos. */
export const FRAME_BUFFER_MAX_BYTES_MAX_MB = 4096;

/**
 * Parse `?frameBufferMaxBytes=N` (MB).
 *
 * - Absent / empty → FRAME_BUFFER_MAX_BYTES_DEFAULT_MB.
 * - `0` → 0 (caller treats as "no byte cap; count cap only").
 * - Negative or non-finite → 0 (disabled, same as explicit 0).
 * - Clamped to FRAME_BUFFER_MAX_BYTES_MAX_MB.
 *
 * Returned value is in MB; the consumer multiplies by 1024 * 1024 when
 * passing through to FrameBuffer.
 */
function parseFrameBufferMaxBytesMB(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return FRAME_BUFFER_MAX_BYTES_DEFAULT_MB;
  }
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, FRAME_BUFFER_MAX_BYTES_MAX_MB);
}

/** Upper bound for `?camerahole=N` (degrees). 180° is "entire sphere transparent". */
export const CAMERA_HOLE_MAX = 180;
/**
 * Default `?camerahole` when the URL omits it (degrees).
 *
 * Pinned at 78° because that exactly reproduces the renderer's
 * historical `HOLE_END_DOT = -0.20` constant: `cos(78°) ≈ 0.2079`, so
 * `-cos(78°) ≈ -0.208 ≈ -0.20`. Changing this default also changes the
 * shipped visual.
 */
export const CAMERA_HOLE_DEFAULT = 78;

/**
 * Parse `?ballRadius=N` (Å). Absent / empty → BALL_RADIUS_DEFAULT.
 * Negative or non-finite → 0 (wall disabled). Explicit `0` is preserved
 * as the "auto" sentinel that tells the backend to size the wall to the
 * loaded system at MD start.
 */
function parseBallRadius(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return BALL_RADIUS_DEFAULT;
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, BALL_RADIUS_MAX);
}

/**
 * Parse `?friction=N` (Langevin gamma in 1/fs). Absent / empty → `null`
 * (no override; default 3-tier resolution applies). Negative / non-finite
 * → `null`. `0` is preserved as the NVE marker. No upper clamp — backend
 * accepts any non-negative value.
 */
function parseFriction(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

/**
 * Parse `?ballK=N` (eV/Å²). Absent / empty → BALL_K_DEFAULT.
 * Negative or non-finite → 0 (wall disabled). Explicit `0` disables the
 * wall.
 */
function parseBallK(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return BALL_K_DEFAULT;
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, BALL_K_MAX);
}

/**
 * Parse `?timestep=N` (MD integrator dt in fs). Absent / empty /
 * non-finite / `<= 0` → `TIMESTEP_FS_DEFAULT` (1.0 fs). A valid positive
 * value is clamped to `[TIMESTEP_FS_MIN, TIMESTEP_FS_MAX]`.
 *
 * Both bounds mirror the backend: `TIMESTEP_FS_MIN` matches `ws_app.py`'s
 * `MIN_TIMESTEP_FS` floor so the wire value equals what runs, and
 * `TIMESTEP_FS_MAX` guards a runaway dt (the backend has no upper clamp —
 * a large dt would only trip the post-hoc `MD_INSTABILITY` guard). An
 * explicit caller-supplied `timestep_fs` (e.g. the nano-reactor tutorial's
 * calibrated 0.5 fs) bypasses this parser entirely and is passed through
 * untouched.
 */
function parseTimestepFs(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return TIMESTEP_FS_DEFAULT;
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num <= 0) return TIMESTEP_FS_DEFAULT;
  return Math.min(Math.max(num, TIMESTEP_FS_MIN), TIMESTEP_FS_MAX);
}

/**
 * Baseline pre-roll / post-roll frames the cinematic always adds on
 * each side of the detected reactant→product window. `?discoverReplay=N`
 * stacks *on top of* this baseline, so the effective per-side padding
 * is `DISCOVER_REPLAY_BASELINE + appParams.discoverReplay`.
 *
 * Exposed for use at the wiring layer (`init.tsx`) and for tests.
 */
export const DISCOVER_REPLAY_BASELINE = 20;

/**
 * Parse `?discoverReplay=N` — *additional* frames to walk on each
 * side of the detected reactant→product window when the reaction
 * cinematic plays, on top of {@link DISCOVER_REPLAY_BASELINE}. Absent
 * / empty / non-numeric / negative → 0 (just the baseline). Clamped
 * to a sensible upper bound (`DISCOVER_REPLAY_MAX`) so a mistyped
 * huge value cannot lock the cinematic on a multi-thousand frame
 * walk.
 */
const DISCOVER_REPLAY_MAX = 600;
function parseDiscoverReplay(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, DISCOVER_REPLAY_MAX);
}

/**
 * Parse `?discoverZoom=N` — whitespace buffer (percent) added around
 * the cinematic's framed reaction. Absent / empty → DISCOVER_ZOOM_DEFAULT.
 * Negative / non-numeric → 0 (camera fits the cluster snugly).
 * Clamped to DISCOVER_ZOOM_MAX so a typo cannot produce a useless,
 * arbitrarily-far view.
 */
const DISCOVER_ZOOM_DEFAULT = 275;
const DISCOVER_ZOOM_MAX = 1000;
function parseDiscoverZoom(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return DISCOVER_ZOOM_DEFAULT;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, DISCOVER_ZOOM_MAX);
}

/** Default `?gameSessionSec` when the URL omits it (seconds). */
export const GAME_SESSION_SEC_DEFAULT = 60;
/** Hard upper clamp for `?gameSessionSec=N` to avoid typos producing infinite rounds. */
const GAME_SESSION_SEC_MAX = 3600;

/** Default `?gameProjectileMin` (live projectiles to maintain). */
export const GAME_PROJECTILE_MIN_DEFAULT = 2;
/** Default `?gameProjectileMax` (renderer-friendly soft cap). */
export const GAME_PROJECTILE_MAX_DEFAULT = 4;
/** Hard cap for projectile counts to keep the renderer well-behaved. */
const GAME_PROJECTILE_HARD_CAP = 32;

/** Default `?gameSpawnCooldownMs`. 400 ms = 2.5 spawns/s burst limit. */
export const GAME_SPAWN_COOLDOWN_MS_DEFAULT = 400;
/** Hard cap for `?gameSpawnCooldownMs` to keep spawn responsiveness reasonable. */
const GAME_SPAWN_COOLDOWN_MS_MAX = 60000;

/** Default `?gameSpawnIntervalMs`. 2000 ms = 1 spawn / 2 s steady-state. */
export const GAME_SPAWN_INTERVAL_MS_DEFAULT = 2000;
/** Hard cap for `?gameSpawnIntervalMs`. */
const GAME_SPAWN_INTERVAL_MS_MAX = 60000;

/** Default `?gameLaunchSpeed` (Å/fs upward per projectile atom). */
/**
 * Default upward launch velocity (Å/fs).
 *
 * Empirical calibration on a CUDA backend: each second of wall clock
 * advances ~1 fs of simulated time at game-mode load (the ML-IP
 * forward pass is the bottleneck, not the integrator). The play
 * area spans ~32 Å between spawn line (-PLAY_Y - 4) and cull line
 * (+PLAY_Y), so to traverse in ~25 s wall-clock at 1.2 Å/fs:
 *   t ≈ 32 Å / (1.2 Å/fs · 1 fs/s) ≈ 27 s
 * All projectiles launch at this exact speed regardless of how many
 * reactions have happened — the spawner intentionally avoids any
 * per-spawn boost so successive launches can't catch up to each
 * other before they enter the viewport. Tune per backend with
 * `?gameLaunchSpeed=N`.
 */
export const GAME_LAUNCH_SPEED_DEFAULT = 1.2;
/** Hard upper bound on launch speed (collision energy guardrail). */
/**
 * Hard cap for `?gameLaunchSpeed=N` in Å/fs. Keeps a typo from
 * sending atoms across the screen in a single MD step (which would
 * blow the pair potential up). Generous enough to allow ≤ 1 s
 * traversal at typical ML-IP throughput.
 */
const GAME_LAUNCH_SPEED_MAX = 50.0;

function parseGameSessionSec(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return GAME_SESSION_SEC_DEFAULT;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 1) return GAME_SESSION_SEC_DEFAULT;
  return Math.min(num, GAME_SESSION_SEC_MAX);
}

function parseGameProjectileCount(
  value: string | null | undefined,
  fallback: number,
): number {
  if (value === null || value === undefined || value === '') return fallback;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 0) return fallback;
  return Math.min(num, GAME_PROJECTILE_HARD_CAP);
}

function parseGameSpawnIntervalMs(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return GAME_SPAWN_INTERVAL_MS_DEFAULT;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 0) return GAME_SPAWN_INTERVAL_MS_DEFAULT;
  return Math.min(num, GAME_SPAWN_INTERVAL_MS_MAX);
}

function parseGameSpawnCooldownMs(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return GAME_SPAWN_COOLDOWN_MS_DEFAULT;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 0) return GAME_SPAWN_COOLDOWN_MS_DEFAULT;
  return Math.min(num, GAME_SPAWN_COOLDOWN_MS_MAX);
}

function parseGameLaunchSpeed(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return GAME_LAUNCH_SPEED_DEFAULT;
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num <= 0) return GAME_LAUNCH_SPEED_DEFAULT;
  return Math.min(num, GAME_LAUNCH_SPEED_MAX);
}

/**
 * Default `?gameInplaneTol` (Å). Half-thickness of the slab's free
 * zone — bigger than 300 K thermal vibrations of heavy atoms (~0.05 Å)
 * and H (~0.2 Å), tight enough to look planar in the top-down camera.
 */
export const GAME_INPLANE_TOLERANCE_DEFAULT = 0.5;
/** Hard cap on `?gameInplaneTol=N` (Å). Past this the slab is functionally a no-op. */
const GAME_INPLANE_TOLERANCE_MAX = 100.0;
/**
 * Default `?gameInplaneK` (eV/Å²). Same order as `containmentK`. Pulls
 * a runaway H back in <0.5 ps; soft enough not to pop bonds.
 */
export const GAME_INPLANE_K_DEFAULT = 10.0;
/** Hard cap on `?gameInplaneK=N` (eV/Å²); shared with the backend's containment_k clamp. */
const GAME_INPLANE_K_MAX = 100.0;

function parseGameInplaneTolerance(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return GAME_INPLANE_TOLERANCE_DEFAULT;
  const num = parseFloat(value);
  // Negative / non-finite → fall back. `0` is a deliberate "off" value
  // (matched on the k-knob too) so it MUST round-trip; any positive
  // value is clamped to the hard cap.
  if (!Number.isFinite(num) || num < 0) return GAME_INPLANE_TOLERANCE_DEFAULT;
  return Math.min(num, GAME_INPLANE_TOLERANCE_MAX);
}

function parseGameInplaneK(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return GAME_INPLANE_K_DEFAULT;
  const num = parseFloat(value);
  // `?gameInplaneK=0` is the documented escape hatch — preserve 0
  // exactly. Negative / non-finite → fall back to the default.
  if (!Number.isFinite(num) || num < 0) return GAME_INPLANE_K_DEFAULT;
  return Math.min(num, GAME_INPLANE_K_MAX);
}

/**
 * Default fade percentage applied to peripheral atoms/bonds during a
 * reaction cinematic when `?discoverFade=` is omitted. `100` ⇒ fully
 * invisible — matches the user-facing intent of "focus the reaction
 * cluster, hide everything else."
 */
export const DISCOVER_FADE_DEFAULT = 100;
const DISCOVER_FADE_MAX = 100;
function parseDiscoverFade(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return DISCOVER_FADE_DEFAULT;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, DISCOVER_FADE_MAX);
}

/**
 * Default number of real (non-interpolated) backend frames a candidate
 * topology must persist before the reaction detector commits it as a
 * "stable" state and emits a reaction. Higher values reject more
 * transient bond fluctuations at the cost of detection latency.
 *
 * `1` reproduces the legacy "emit on every diff" behaviour; tests that
 * drive synthetic chemistry use that value via `?stabilitySteps=1` so
 * they don't have to wait for the gate.
 */
export const STABILITY_STEPS_DEFAULT = 10;
function parseStabilitySteps(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return STABILITY_STEPS_DEFAULT;
  const num = parseInt(value, 10);
  if (!Number.isFinite(num)) return STABILITY_STEPS_DEFAULT;
  return Math.max(1, Math.floor(num));
}

/**
 * Parse `?camerahole=N` (degrees, half-angle of the transparent cap on
 * the camera-facing side of the containment-sphere render).
 *
 * Absent / empty → `CAMERA_HOLE_DEFAULT`.
 * Negative / non-finite → `0` (no transparent cap).
 * Clamped to `CAMERA_HOLE_MAX` (180°) on the high end.
 */
function parseCameraHole(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return CAMERA_HOLE_DEFAULT;
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.min(num, CAMERA_HOLE_MAX);
}

/**
 * Parse jitter buffer delay from URL param
 * Valid values: 0-1000 (ms)
 * Default: 700ms (enabled by default)
 * ?jitter=0 explicitly disables
 */
function parseJitter(value: string | null | undefined, hasParam: boolean): number {
  // No param at all → default enabled at 700ms
  if (!hasParam) return 700;
  // Param present but empty (?jitter) → default 700ms
  if (value === '' || value === null || value === undefined) return 700;
  const num = parseInt(value, 10);
  // Invalid number → default 700ms
  if (Number.isNaN(num)) return 700;
  // Explicit 0 → disabled
  if (num === 0) return 0;
  // Negative → disabled
  if (num < 0) return 0;
  // Clamp to 1000ms max
  return Math.min(num, 1000);
}

/**
 * Parse all application parameters from URL
 * 
 * Should be called once at app startup (init.tsx).
 * Caches result for subsequent getAppParams() calls.
 * 
 * @param win - Window object (defaults to global window)
 * @returns Parsed AppParams
 */
export function parseAppParams(win?: Window | null): AppParams {
  const w = win ?? (typeof window !== 'undefined' ? window : null);
  
  // Delegate loading params to urlParser (handles base64 decoding, SMILES validation)
  const loadingParams: URLParams = parseLoadingParams(w);
  
  // Parse remaining params directly
  const urlParams = w ? new URLSearchParams(w.location.search) : null;
  
  // Global debug flag
  const globalDebug = urlParams?.get('debug') === '1';

  const params: AppParams = {
    // Loading (from urlParser - handles decoding)
    load: loadingParams.load ?? null,
    molxyz: loadingParams.molxyz ?? null,
    smiles: loadingParams.smiles ?? null,

    // Debug
    debug: globalDebug,
    debugCategories: {
      select: globalDebug || urlParams?.get('selectdebug') === '1',
      drag: globalDebug || urlParams?.get('dragdebug') === '1',
      factory: globalDebug || urlParams?.get('factorydebug') === '1',
      frame: globalDebug || urlParams?.get('framedebug') === '1',
      timeline: globalDebug || urlParams?.get('timelinedebug') === '1',
      ws: globalDebug || urlParams?.get('wsdebug') === '1',
      sim: globalDebug || urlParams?.get('simdebug') === '1',
      loader: globalDebug || urlParams?.get('loaderdebug') === '1',
      render: globalDebug || urlParams?.get('renderdebug') === '1',
      topology: globalDebug || urlParams?.get('topologydebug') === '1',
      budget: globalDebug || urlParams?.get('budgetdebug') === '1' || urlParams?.get('debugBudget') === '1',
      naming: globalDebug || urlParams?.get('namingdebug') === '1' || urlParams?.get('namingDebug') === '1',
      'drag-predict':
        globalDebug ||
        urlParams?.get('dragpredictdebug') === '1' ||
        urlParams?.get('dragPredictDebug') === '1',
    },

    // Features
    bondVerify: urlParams?.get('bondVerify') === '1',
    // TEMPORARY: Default to true for bond correctness. Disable with ?recomputeBondEveryFrame=0
    // Will be reverted in later PR.
    recomputeBondsEveryUpdate: urlParams?.get('recomputeBondsEveryUpdate') !== '0' && urlParams?.get('recomputeBondEveryFrame') !== '0',
    recomputeBondEveryFrame: urlParams?.get('recomputeBondEveryFrame') !== '0' && urlParams?.get('recomputeBondsEveryUpdate') !== '0',

    // Behavior
    edit: urlParams?.get('edit') === '1',
    playbackURL: urlParams?.get('playbackURL') ?? null,
    labels: urlParams?.has('labels') ? urlParams.get('labels') !== '0' : !urlParams?.has('playbackURL'),
    interactive: urlParams?.has('interactive') ? urlParams.get('interactive') !== '0' : !urlParams?.has('playbackURL'),
    fancyShading: urlParams?.get('fancyShading') === '1',
    experimental: urlParams?.get('experimental') === '1',
    // Edit mode and deep-links imply noTutorial. `?tutorial=1` is an
    // explicit opt-out that keeps the tutorial on top of a deep-linked
    // system; explicit `?noTutorial` and `?edit=1` always win.
    noTutorial:
      !!(urlParams?.has('noTutorial') ||
      urlParams?.get('noTutorial') === '1' ||
      urlParams?.get('edit') === '1' ||
      urlParams?.get('gameMode') === '1' ||
      urlParams?.has('playbackURL') ||
      ((urlParams?.has('load') ||
        urlParams?.has('molxyz') ||
        urlParams?.has('smiles')) &&
        urlParams?.get('tutorial') !== '1')),
    // Launch a specific (possibly hidden) tutorial module by id.
    tutorialModule: urlParams?.get('tutorialModule') ?? null,
    // Panel-open default. See AppParams.panelsOpenDefault for the full rules
    // — this mirrors the noTutorial logic MINUS the implicit deep-link
    // branch, so a bare `?load=…` keeps the panels closed (kiosk-style)
    // while `?load=…&noTutorial=1` (or `&panelsOpen=1`) opens them.
    panelsOpenDefault: (() => {
      const explicit = urlParams?.get('panelsOpen');
      if (explicit === '1') return true;
      if (explicit === '0') return false;
      return Boolean(
        urlParams?.has('noTutorial') ||
        urlParams?.get('noTutorial') === '1' ||
        urlParams?.get('edit') === '1' ||
        urlParams?.get('gameMode') === '1',
      );
    })(),
    alwaysShowTimeline: urlParams?.get('alwaysShowTimeline') === '1',
    autoMD: urlParams?.get('autoMD') !== '0' && urlParams?.get('edit') !== '1',  // true unless explicitly disabled or in edit mode
    noAutoLoad: urlParams?.get('noAutoLoad') === '1',
    // Naming pipeline opt-out: only the literal '0' disables.
    naming: urlParams?.get('naming') !== '0',
    namingBreakerCooldownMs: (() => {
      const raw = urlParams?.get('namingBreakerCooldownMs');
      if (raw === null || raw === undefined || raw === '') return null;
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n < 0) return null;
      return n;
    })(),
    // Always on. The reaction-detection / banner / click-to-replay
    // cinematic stack is permanently wired now — the legacy URL flag
    // is ignored, and there is no runtime toggle.
    discoverChemistry: true,
    stabilitySteps: parseStabilitySteps(urlParams?.get('stabilitySteps')),
    discoverReplay: parseDiscoverReplay(urlParams?.get('discoverReplay')),
    discoverZoom: parseDiscoverZoom(urlParams?.get('discoverZoom')),
    discoverFade: parseDiscoverFade(urlParams?.get('discoverFade')),
    gameMode: urlParams?.get('gameMode') === '1',
    gameSessionSec: parseGameSessionSec(urlParams?.get('gameSessionSec')),
    gameProjectileMin: parseGameProjectileCount(
      urlParams?.get('gameProjectileMin'),
      GAME_PROJECTILE_MIN_DEFAULT,
    ),
    gameProjectileMax: parseGameProjectileCount(
      urlParams?.get('gameProjectileMax'),
      GAME_PROJECTILE_MAX_DEFAULT,
    ),
    gameSpawnCooldownMs: parseGameSpawnCooldownMs(urlParams?.get('gameSpawnCooldownMs')),
    gameSpawnIntervalMs: parseGameSpawnIntervalMs(urlParams?.get('gameSpawnIntervalMs')),
    gameLaunchSpeed: parseGameLaunchSpeed(urlParams?.get('gameLaunchSpeed')),
    gameInplaneTolerance: parseGameInplaneTolerance(urlParams?.get('gameInplaneTol')),
    gameInplaneK: parseGameInplaneK(urlParams?.get('gameInplaneK')),

    // Performance
    // In game mode the player needs immediate, position-accurate
    // feedback for placement/drag/launch (the cinematic-replay
    // smoothness that interpolation provides matters less than the
    // sub-RAF responsiveness it costs). Default interpolation to OFF
    // when `?gameMode=1` is set unless the user explicitly passes
    // `?interpolation=N`. Mirrors the `gameMode → noTutorial` default
    // override pattern above.
    interpolation:
      urlParams?.get('gameMode') === '1' && !urlParams?.has('interpolation')
        ? 0
        : parseInterpolationLevel(urlParams?.get('interpolation')),

    // Testing
    addLatency: parseAddLatency(urlParams?.get('addLatency')),

    // Simulation overrides
    friction: parseFriction(urlParams?.get('friction')),
    timestepFs: parseTimestepFs(urlParams?.get('timestep')),

    // Jitter buffer
    jitter: parseJitter(urlParams?.get('jitter'), urlParams?.has('jitter') ?? false),
    debugJitter: urlParams?.get('debugJitter') === '1',

    // Containment ball
    ballRadius: parseBallRadius(urlParams?.get('ballRadius')),
    ballRadiusPinned: !!(urlParams?.has('ballRadius')),
    ballK: parseBallK(urlParams?.get('ballK')),
    // Nano-reactor: always off at page-load. Defaults give a gentle
    // compression cycle (R range 1-7 Å, 300 steps ≈ 0.3 s @ 1 fs) —
    // long enough for the breathing motion to feel deliberate without
    // overdriving the wall force on first toggle. Paired with the
    // 5.0 BALL_K_DEFAULT for a forgiving "what does this knob do"
    // first experience.
    nanoReactorEnabled: false,
    nanoReactorRMin: 1,
    nanoReactorRMax: 7,
    nanoReactorPeriodSteps: 300,
    // Charge / spin: neutral singlet at page-load; set via the Charge &
    // Spin panel. No URL override (these are interactive-only knobs).
    charge: CHARGE_DEFAULT,
    spin: SPIN_DEFAULT,
    // `?camerahole=` (preferred lowercase) with `?cameraHole=` accepted
    // as a camelCase alias for consistency with other ball-related params.
    cameraHole: parseCameraHole(urlParams?.get('camerahole') ?? urlParams?.get('cameraHole')),

    // Timeline buffer
    frameBufferMaxBytes: parseFrameBufferMaxBytesMB(urlParams?.get('frameBufferMaxBytes')),
  };

  cachedParams = params;
  return params;
}

/**
 * Get cached application parameters
 * 
 * @throws Error if parseAppParams() hasn't been called yet
 * @returns Cached AppParams
 */
export function getAppParams(): AppParams {
  if (!cachedParams) {
    // Auto-initialize if not done yet (graceful fallback)
    console.warn('[appParams] getAppParams() called before parseAppParams(). Auto-initializing.');
    return parseAppParams();
  }
  return cachedParams;
}

/**
 * Check if app params have been initialized
 */
export function isAppParamsInitialized(): boolean {
  return cachedParams !== null;
}

/**
 * Reset cached params (for testing)
 */
export function resetAppParamsForTesting(): void {
  cachedParams = null;
}

/**
 * Set params directly (for testing)
 */
export function setAppParamsForTesting(params: Partial<AppParams>): void {
  const defaults = createDefaultAppParams();
  cachedParams = { ...defaults, ...params };
}

/**
 * Create default AppParams (all disabled)
 */
export function createDefaultAppParams(): AppParams {
  return {
    load: null,
    molxyz: null,
    smiles: null,
    debug: false,
    debugCategories: {
      select: false,
      drag: false,
      factory: false,
      frame: false,
      timeline: false,
      ws: false,
      sim: false,
      loader: false,
      render: false,
      topology: false,
      budget: false,
      naming: false,
      'drag-predict': false,
    },
    bondVerify: false,
    recomputeBondsEveryUpdate: false,
    recomputeBondEveryFrame: false,
    noTutorial: false,
    tutorialModule: null,
    panelsOpenDefault: false,
    alwaysShowTimeline: false,
    autoMD: true,
    noAutoLoad: false,
    naming: true,
    namingBreakerCooldownMs: null,
    discoverChemistry: true,
    stabilitySteps: STABILITY_STEPS_DEFAULT,
    discoverReplay: 0,
    discoverZoom: 275,
    discoverFade: DISCOVER_FADE_DEFAULT,
    gameMode: false,
    gameSessionSec: GAME_SESSION_SEC_DEFAULT,
    gameProjectileMin: GAME_PROJECTILE_MIN_DEFAULT,
    gameProjectileMax: GAME_PROJECTILE_MAX_DEFAULT,
    gameSpawnCooldownMs: GAME_SPAWN_COOLDOWN_MS_DEFAULT,
    gameSpawnIntervalMs: GAME_SPAWN_INTERVAL_MS_DEFAULT,
    gameLaunchSpeed: GAME_LAUNCH_SPEED_DEFAULT,
    gameInplaneTolerance: GAME_INPLANE_TOLERANCE_DEFAULT,
    gameInplaneK: GAME_INPLANE_K_DEFAULT,
    interpolation: 3,
    addLatency: 0,
    friction: null,
    timestepFs: TIMESTEP_FS_DEFAULT,
    jitter: 700,
    debugJitter: false,
    edit: false,
    playbackURL: null,
    labels: true,
    interactive: true,
    fancyShading: false,
    experimental: false,
    ballRadius: 0,
    ballRadiusPinned: false,
    ballK: 0,
    nanoReactorEnabled: false,
    nanoReactorRMin: 1,
    nanoReactorRMax: 7,
    nanoReactorPeriodSteps: 300,
    charge: CHARGE_DEFAULT,
    spin: SPIN_DEFAULT,
    cameraHole: CAMERA_HOLE_DEFAULT,
    frameBufferMaxBytes: 0,
  };
}

/**
 * Update the cached ball radius (Å) at runtime.
 *
 * The new value is clamped exactly the way the URL parser would clamp
 * it (see :func:`parseBallRadius`), and dispatches a
 * `mlip:ballRadiusChanged` event so live consumers (e.g. the
 * SimulationPanel badge, future slider) can react.
 *
 * Returns the clamped value that actually got stored.
 */
/**
 * Enable (or disable) experimental mode at runtime.
 *
 * Used so that launching an experimental-tier tutorial (module number ≥ 100)
 * can imply `?experimental=1` — turning on experimental UI like the
 * tractor-beam panel — without the caller having to add the URL flag.
 * Idempotent; only flips the cached flag.
 */
export function setExperimentalRuntime(value: boolean): void {
  getAppParams().experimental = value;
}

export function setBallRadiusRuntime(value: number): number {
  const params = getAppParams();
  let v = Number(value);
  if (!Number.isFinite(v) || v < 0) v = 0;
  v = Math.min(v, BALL_RADIUS_MAX);
  params.ballRadius = v;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:ballRadiusChanged', { detail: { value: v } }),
    );
  }
  return v;
}

/**
 * Update the cached ball stiffness (eV/Å²) at runtime.
 *
 * Same shape as :func:`setBallRadiusRuntime`. Setting `0` disables the
 * wall on the next MD start.
 */
export function setBallKRuntime(value: number): number {
  const params = getAppParams();
  let v = Number(value);
  if (!Number.isFinite(v) || v < 0) v = 0;
  v = Math.min(v, BALL_K_MAX);
  params.ballK = v;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:ballKChanged', { detail: { value: v } }),
    );
  }
  return v;
}

/**
 * Update the cached total charge (e) at runtime. Clamps to the slider
 * bounds [CHARGE_MIN, CHARGE_MAX] and rounds to an integer. Dispatches
 * `mlip:chargeChanged` so live consumers (simulationRuntime) can rebuild
 * outbound StartParams. Returns the clamped value.
 */
export function setChargeRuntime(value: number): number {
  const params = getAppParams();
  let v = Math.round(Number(value));
  if (!Number.isFinite(v)) v = CHARGE_DEFAULT;
  v = Math.max(CHARGE_MIN, Math.min(CHARGE_MAX, v));
  params.charge = v;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:chargeChanged', { detail: { value: v } }),
    );
  }
  return v;
}

/**
 * Update the cached spin multiplicity at runtime. Clamps to the slider
 * bounds [SPIN_MIN, SPIN_MAX] and rounds to an integer. Dispatches
 * `mlip:spinChanged`. Returns the clamped value.
 */
export function setSpinRuntime(value: number): number {
  const params = getAppParams();
  let v = Math.round(Number(value));
  if (!Number.isFinite(v)) v = SPIN_DEFAULT;
  v = Math.max(SPIN_MIN, Math.min(SPIN_MAX, v));
  params.spin = v;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:spinChanged', { detail: { value: v } }),
    );
  }
  return v;
}

/**
 * Latest backend-authoritative charge / spin echo from a Frame. The
 * frontend's `appParams.charge/spin` are the user *intent*; the backend
 * may clamp them to its own range, so the panel renders this echoed pair
 * (when present) to stay backend-authoritative. Either field may be
 * `undefined` when the backend used its task-aware default for that field.
 */
export interface LastChargeSpinState {
  charge?: number;
  spin?: number;
}
let _lastChargeSpinState: LastChargeSpinState | null = null;

export function getLastChargeSpinState(): LastChargeSpinState | null {
  return _lastChargeSpinState;
}

/**
 * Record the latest backend-reported charge / spin. Called from
 * `normalizeChargeSpin` (transport) on every Frame that carries an
 * explicit value. Early-returns when the pair is unchanged so the
 * per-frame call is cheap. Dispatches `mlip:chargeSpinFrameState`.
 */
export function setLastChargeSpinState(state: LastChargeSpinState): void {
  const prev = _lastChargeSpinState;
  if (prev && prev.charge === state.charge && prev.spin === state.spin) return;
  _lastChargeSpinState = { charge: state.charge, spin: state.spin };
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:chargeSpinFrameState', { detail: _lastChargeSpinState }),
    );
  }
}

/**
 * Returns true iff the user pinned the containment radius via the URL
 * `?ballRadius=` parameter at startup. When pinned, file-load and
 * session-restore code paths must NOT mutate `appParams.ballRadius`.
 *
 * Slider drags still call `setBallRadiusRuntime` directly — they do
 * not consult this flag. The flag exists to short-circuit *implicit*
 * mutations from file metadata.
 */
export function isBallRadiusPinned(): boolean {
  return !!getAppParams().ballRadiusPinned;
}

/**
 * Last containment radius reported by the backend on a Frame.
 *
 * The frontend's `appParams.ballRadius` is the user/file *intent*: it
 * may be `0` (the "auto" sentinel that asks the backend to size the
 * wall to the loaded system at MD start). The backend then resolves a
 * concrete radius and echoes it on every frame's `containmentRadius`
 * field. The transport layer (`normalizeContainmentState`) calls
 * `setLastResolvedBallRadius` whenever a positive value lands, so any
 * UI that wants to show "what's actually in effect right now" can read
 * this value via `getLastResolvedBallRadius()`.
 *
 * The slider in `SimulationPanel` uses this to display a numeric value
 * even when the user picked the auto sentinel — per the policy
 * "slider always reflects the current actual numeric value".
 */
let _lastResolvedBallRadius: number | null = null;

/** Returns the last positive `containmentRadius` echoed by the backend, or `null`. */
export function getLastResolvedBallRadius(): number | null {
  return _lastResolvedBallRadius;
}

/**
 * Record the latest backend-resolved containment radius. Called from
 * `normalizeContainmentState` (transport) on every Frame that carries a
 * positive `containmentRadius`. Dispatches `mlip:resolvedBallRadiusChanged`
 * so the slider can update without polling.
 */
export function setLastResolvedBallRadius(value: number): void {
  if (!Number.isFinite(value) || value <= 0) return;
  if (_lastResolvedBallRadius === value) return;
  _lastResolvedBallRadius = value;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:resolvedBallRadiusChanged', { detail: { value } }),
    );
  }
}

/**
 * Update the nano-reactor user intent at runtime.
 *
 * Setting `enabled = true` while `ballK === 0` is silently allowed —
 * the backend will drop the schedule. The panel UI is responsible for
 * keeping the toggle disabled in that state; this function only owns
 * the cache + event dispatch.
 *
 * Dispatches `mlip:nanoReactorChanged` (intent change, slider drag,
 * toggle flip) so live consumers can rebuild outbound StartParams.
 * The server-reported state echo flows via `mlip:nanoReactorFrameState`
 * — see :func:`setLastNanoReactorFrameState`.
 */
export function setNanoReactorRuntime(
  patch: Partial<{
    enabled: boolean;
    rMin: number;
    rMax: number;
    periodSteps: number;
  }>,
): void {
  const params = getAppParams();
  if (patch.enabled !== undefined) params.nanoReactorEnabled = !!patch.enabled;
  if (patch.rMin !== undefined && Number.isFinite(patch.rMin) && patch.rMin > 0) {
    params.nanoReactorRMin = patch.rMin;
  }
  if (patch.rMax !== undefined && Number.isFinite(patch.rMax) && patch.rMax > 0) {
    params.nanoReactorRMax = patch.rMax;
  }
  if (
    patch.periodSteps !== undefined &&
    Number.isFinite(patch.periodSteps) &&
    patch.periodSteps > 0
  ) {
    params.nanoReactorPeriodSteps = Math.floor(patch.periodSteps);
  }
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:nanoReactorChanged', {
        detail: {
          enabled: params.nanoReactorEnabled,
          rMin: params.nanoReactorRMin,
          rMax: params.nanoReactorRMax,
          periodSteps: params.nanoReactorPeriodSteps,
        },
      }),
    );
  }
}

/**
 * Latest nano-reactor frame echo from the backend. Mirrors the
 * `Frame.nanoreactor` shape so the panel's live status display can
 * read schedule + phase + current radius without polling.
 *
 * Cleared (set to `null`) on every system load — see :func:`clearLastNanoReactorFrameState`.
 */
export interface LastNanoReactorFrameState {
  rMin: number;
  rMax: number;
  periodSteps: number;
  phase: number;
  rNow: number;
}
let _lastNanoReactorFrameState: LastNanoReactorFrameState | null = null;

export function getLastNanoReactorFrameState(): LastNanoReactorFrameState | null {
  return _lastNanoReactorFrameState;
}

/**
 * Record the latest backend-reported nano-reactor state. Called from
 * `normalizeContainmentState` (transport) on every Frame that carries
 * the `nanoreactor` substructure. Dispatches
 * `mlip:nanoReactorFrameState` so the panel can re-render without
 * polling frame state.
 */
export function setLastNanoReactorFrameState(state: LastNanoReactorFrameState): void {
  _lastNanoReactorFrameState = state;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:nanoReactorFrameState', { detail: state }),
    );
  }
}

/**
 * Clear the cached frame echo. Called by the system loader so a fresh
 * system never inherits the previous run's reactor display. Dispatches
 * a `mlip:nanoReactorFrameState` with `null` detail so subscribers can
 * reset their UI accordingly.
 */
export function clearLastNanoReactorFrameState(): void {
  if (_lastNanoReactorFrameState === null) return;
  _lastNanoReactorFrameState = null;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent('mlip:nanoReactorFrameState', { detail: null }),
    );
  }
}

/** Test-only reset hook. Not exported from the barrel. */
export function _resetLastResolvedBallRadiusForTest(): void {
  _lastResolvedBallRadius = null;
}

/** Test-only reset hook for the charge/spin echo cache. Not in the barrel. */
export function _resetLastChargeSpinStateForTest(): void {
  _lastChargeSpinState = null;
}

/**
 * Test-only setter for the URL-pin flag. Not exported from the barrel.
 *
 * Production code never flips this — it's set once in `parseAppParams`
 * from the presence of `?ballRadius=` in the URL. Tests use this hook
 * to simulate a URL-pinned page without round-tripping through the
 * full param parser.
 */
export function _setBallRadiusPinnedForTest(value: boolean): void {
  getAppParams().ballRadiusPinned = value;
}
